import httpClient from '../httpClient';

// ===== INTERFACES =====

export interface ChangelogItem {
  id?: number;
  type: 'feature' | 'improvement' | 'bugfix' | 'breaking';
  description: string;
  sort_order: number;
}

export interface ChangelogEntry {
  uuid: string;
  version: string;
  title: string;
  description: string;
  published_date: string;
  status: 'draft' | 'published';
  items: ChangelogItem[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface Faq {
  uuid: string;
  question: string;
  answer: string;
  category: string;
  sort_order: number;
  is_active: boolean | number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface Article {
  uuid: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  category: string;
  tags: string;
  cover_image: string;
  status: 'draft' | 'published';
  sort_order: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: Pagination;
}

// ===== ADMIN CHANGELOG ENDPOINTS =====

export const getAdminChangelog = async (
  page = 1, limit = 20, search = '', status = ''
): Promise<{ success: boolean; data?: ChangelogEntry[]; pagination?: Pagination; error?: { message: string } | null }> => {
  try {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    const response = await httpClient.get<ApiResponse<ChangelogEntry[]>>(`/admin/knowledge-base?${params}`);
    return { success: true, data: response.data || [], pagination: response.pagination, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to fetch changelog' } };
  }
};

export const getAdminChangelogEntry = async (uuid: string): Promise<{ success: boolean; data?: ChangelogEntry; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.get<ApiResponse<ChangelogEntry>>(`/admin/knowledge-base/${uuid}`);
    return { success: true, data: response.data, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to fetch changelog entry' } };
  }
};

export const createChangelogEntry = async (data: {
  version: string; title: string; description?: string; published_date: string; status?: string; items?: Partial<ChangelogItem>[];
}): Promise<{ success: boolean; data?: ChangelogEntry; message?: string; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.post<ApiResponse<ChangelogEntry>>('/admin/knowledge-base', data);
    return { success: true, data: response.data, message: response.message, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to create changelog entry' } };
  }
};

export const updateChangelogEntry = async (uuid: string, data: Partial<{
  version: string; title: string; description: string; published_date: string; status: string; items: Partial<ChangelogItem>[];
}>): Promise<{ success: boolean; data?: ChangelogEntry; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.put<ApiResponse<ChangelogEntry>>(`/admin/knowledge-base/${uuid}`, data);
    return { success: true, data: response.data, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to update changelog entry' } };
  }
};

export const publishChangelogEntry = async (uuid: string): Promise<{ success: boolean; message?: string; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.put<ApiResponse<null>>(`/admin/knowledge-base/${uuid}/publish`);
    return { success: true, message: response.message, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to publish' } };
  }
};

export const unpublishChangelogEntry = async (uuid: string): Promise<{ success: boolean; message?: string; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.put<ApiResponse<null>>(`/admin/knowledge-base/${uuid}/unpublish`);
    return { success: true, message: response.message, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to unpublish' } };
  }
};

export const deleteChangelogEntry = async (uuid: string): Promise<{ success: boolean; message?: string; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.delete<ApiResponse<null>>(`/admin/knowledge-base/${uuid}`);
    return { success: true, message: response.message, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to delete' } };
  }
};

// ===== ADMIN FAQ ENDPOINTS =====

export const getAdminFaqs = async (search = '', category = ''): Promise<{ success: boolean; data?: Faq[]; error?: { message: string } | null }> => {
  try {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (category) params.append('category', category);
    const response = await httpClient.get<ApiResponse<Faq[]>>(`/admin/knowledge-base/faqs/list?${params}`);
    return { success: true, data: response.data || [], error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to fetch FAQs' } };
  }
};

export const createFaq = async (data: {
  question: string; answer: string; category?: string; sort_order?: number; is_active?: boolean;
}): Promise<{ success: boolean; data?: Faq; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.post<ApiResponse<Faq>>('/admin/knowledge-base/faqs', data);
    return { success: true, data: response.data, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to create FAQ' } };
  }
};

export const updateFaq = async (uuid: string, data: Partial<{
  question: string; answer: string; category: string; sort_order: number; is_active: boolean;
}>): Promise<{ success: boolean; data?: Faq; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.put<ApiResponse<Faq>>(`/admin/knowledge-base/faqs/${uuid}`, data);
    return { success: true, data: response.data, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to update FAQ' } };
  }
};

export const deleteFaq = async (uuid: string): Promise<{ success: boolean; message?: string; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.delete<ApiResponse<null>>(`/admin/knowledge-base/faqs/${uuid}`);
    return { success: true, message: response.message, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to delete FAQ' } };
  }
};

// ===== ADMIN ARTICLE ENDPOINTS =====

export const getAdminArticles = async (
  page = 1, limit = 20, search = '', category = '', status = ''
): Promise<{ success: boolean; data?: Article[]; pagination?: Pagination; error?: { message: string } | null }> => {
  try {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (search) params.append('search', search);
    if (category) params.append('category', category);
    if (status) params.append('status', status);
    const response = await httpClient.get<ApiResponse<Article[]>>(`/admin/knowledge-base/articles/list?${params}`);
    return { success: true, data: response.data || [], pagination: response.pagination, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to fetch articles' } };
  }
};

export const getAdminArticle = async (uuid: string): Promise<{ success: boolean; data?: Article; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.get<ApiResponse<Article>>(`/admin/knowledge-base/articles/${uuid}`);
    return { success: true, data: response.data, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to fetch article' } };
  }
};

export const createArticle = async (data: {
  title: string; content: string; slug?: string; excerpt?: string; category?: string; tags?: string; cover_image?: string; status?: string; sort_order?: number;
}): Promise<{ success: boolean; data?: Article; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.post<ApiResponse<Article>>('/admin/knowledge-base/articles', data);
    return { success: true, data: response.data, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to create article' } };
  }
};

export const updateArticle = async (uuid: string, data: Partial<Article>): Promise<{ success: boolean; data?: Article; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.put<ApiResponse<Article>>(`/admin/knowledge-base/articles/${uuid}`, data);
    return { success: true, data: response.data, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to update article' } };
  }
};

export const deleteArticle = async (uuid: string): Promise<{ success: boolean; message?: string; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.delete<ApiResponse<null>>(`/admin/knowledge-base/articles/${uuid}`);
    return { success: true, message: response.message, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to delete article' } };
  }
};

// ===== PUBLIC (no auth required) =====

export const getPublishedChangelog = async (): Promise<{ success: boolean; data?: ChangelogEntry[]; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.get<ApiResponse<ChangelogEntry[]>>('/knowledge-base/changelog');
    return { success: true, data: response.data || [], error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to fetch changelog' } };
  }
};

export const getActiveFaqs = async (): Promise<{ success: boolean; data?: Faq[]; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.get<ApiResponse<Faq[]>>('/knowledge-base/faqs');
    return { success: true, data: response.data || [], error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to fetch FAQs' } };
  }
};

export const getPublishedArticles = async (): Promise<{ success: boolean; data?: Article[]; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.get<ApiResponse<Article[]>>('/knowledge-base/articles');
    return { success: true, data: response.data || [], error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to fetch articles' } };
  }
};
